import { useCallback, useEffect, useMemo, useState } from "react";
import "./App.css";
import "./ChangeLog.css";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const CHANGE_TYPES = [
  { value: "enhancement", label: "Enhancement" },
  { value: "new-functionality", label: "New functionality" },
  { value: "defect-fix", label: "Defect fix" },
];

const MAX_ATTACHMENT_BYTES = 4 * 1024 * 1024;

async function apiRequest(path, options = {}) {
  const method = (options.method || "GET").toUpperCase();
  const headers = options.body != null || method !== "GET"
    ? { "Content-Type": "application/json", ...(options.headers || {}) }
    : { ...(options.headers || {}) };

  const response = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const text = await response.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error("API returned invalid JSON.");
  }
  if (!response.ok) {
    throw new Error(data?.error || data?.message || `API error ${response.status}`);
  }
  return data;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(new Error(`Could not read file ${file.name}`));
    reader.readAsDataURL(file);
  });
}

function isQuestionVisible(question, answers) {
  if (!question.showIf) return true;
  return answers[question.showIf.questionId] === question.showIf.equals;
}

function changeTypeLabel(value) {
  return CHANGE_TYPES.find(t => t.value === value)?.label || value || "-";
}

function formatDateTime(value) {
  if (!value) return "-";
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? String(value) : new Date(parsed).toLocaleString();
}

function answerDisplay(question, value) {
  if (question?.type === "boolean" || typeof value === "boolean") {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return "Not answered";
  }
  if (Array.isArray(value)) return value.length ? value.join(", ") : "None";
  return value == null || value === "" ? "Not answered" : String(value);
}

function Field({ label, required, children, hint, span2 }) {
  return (
    <div className={`cl-field${span2 ? " cl-span-2" : ""}`} style={span2 ? { gridColumn: "1 / -1" } : undefined}>
      <label className="cl-label">
        {label} {required && <span className="cl-req">*</span>}
      </label>
      {children}
      {hint && <div className="cl-hint">{hint}</div>}
    </div>
  );
}

function BooleanToggle({ value, onChange, disabled }) {
  return (
    <div className="cl-toggle">
      <button
        type="button"
        className={`cl-toggle-btn${value === true ? " cl-toggle-btn-active" : ""}`}
        disabled={disabled}
        onClick={() => onChange(true)}
      >
        Yes
      </button>
      <button
        type="button"
        className={`cl-toggle-btn${value === false ? " cl-toggle-btn-active" : ""}`}
        disabled={disabled}
        onClick={() => onChange(false)}
      >
        No
      </button>
    </div>
  );
}

export default function ChangeLog() {
  const [view, setView] = useState("entries");

  // Shared config
  const [checklists, setChecklists] = useState(null);
  const [configError, setConfigError] = useState("");

  // Entries list
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesError, setEntriesError] = useState("");
  const [filterApp, setFilterApp] = useState("");
  const [search, setSearch] = useState("");
  const [selectedEntry, setSelectedEntry] = useState(null);

  // New entry form
  const [application, setApplication] = useState("");
  const [changeType, setChangeType] = useState("");
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [sprints, setSprints] = useState([]);
  const [sprintsLoaded, setSprintsLoaded] = useState(false);
  const [sprintsLoading, setSprintsLoading] = useState(false);
  const [sprintId, setSprintId] = useState("");
  const [stories, setStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(false);
  const [storySysId, setStorySysId] = useState("");
  const [crNumber, setCrNumber] = useState("");
  const [crStatus, setCrStatus] = useState(null); // null | "checking" | {valid, ...} | {error}
  const [productionDate, setProductionDate] = useState("");
  const [peerReviewer, setPeerReviewer] = useState("");
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({}); // questionId -> File
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitResult, setSubmitResult] = useState(null);

  const applications = useMemo(() => checklists?.applications || [], [checklists]);
  const selectedApp = useMemo(
    () => applications.find(a => a.id === application) || null,
    [applications, application]
  );

  const loadEntries = useCallback(async () => {
    setEntriesLoading(true);
    setEntriesError("");
    try {
      const data = await apiRequest("/change-log");
      setEntries(data.entries || []);
    } catch (e) {
      setEntriesError(e.message);
    } finally {
      setEntriesLoading(false);
    }
  }, []);

  useEffect(() => {
    apiRequest("/change-log/checklists")
      .then(setChecklists)
      .catch(e => setConfigError(e.message));
    loadEntries();
  }, [loadEntries]);

  // Load sprints the first time the form opens
  useEffect(() => {
    if (view !== "new" || sprintsLoaded || sprintsLoading) return;
    setSprintsLoading(true);
    apiRequest("/sn-sprints")
      .then(data => { setSprints(data.sprints || []); setSprintsLoaded(true); })
      .catch(e => setSubmitError(`Could not load sprints: ${e.message}`))
      .finally(() => setSprintsLoading(false));
  }, [view, sprintsLoaded, sprintsLoading]);

  // Load stories when the sprint changes
  useEffect(() => {
    if (!sprintId) {
      setStories([]);
      setStorySysId("");
      return;
    }
    let cancelled = false;
    setStoriesLoading(true);
    setStorySysId("");
    apiRequest(`/sn-stories?sprintId=${encodeURIComponent(sprintId)}`)
      .then(data => { if (!cancelled) setStories(data.stories || []); })
      .catch(e => { if (!cancelled) setSubmitError(`Could not load stories: ${e.message}`); })
      .finally(() => { if (!cancelled) setStoriesLoading(false); });
    return () => { cancelled = true; };
  }, [sprintId]);

  const setAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleApplicationChange = (appId) => {
    setApplication(appId);
    setAnswers({});
    setFiles({});
  };

  const handleValidateCr = async () => {
    const number = crNumber.trim().toUpperCase();
    if (!number) return;
    setCrStatus("checking");
    try {
      const data = await apiRequest(`/change-log/validate-cr?number=${encodeURIComponent(number)}`);
      setCrStatus(data);
    } catch (e) {
      setCrStatus({ error: e.message });
    }
  };

  const resetForm = () => {
    setApplication("");
    setChangeType("");
    setSummary("");
    setDescription("");
    setSprintId("");
    setStorySysId("");
    setCrNumber("");
    setCrStatus(null);
    setProductionDate("");
    setPeerReviewer("");
    setAnswers({});
    setFiles({});
    setSubmitError("");
  };

  const missingRequired = useMemo(() => {
    if (!selectedApp) return [];
    const missing = [];
    if (!changeType) missing.push("Change type");
    if (!summary.trim()) missing.push("Summary");
    if (!storySysId) missing.push("Story");
    if (!crNumber.trim()) missing.push("Change Request");
    if (!productionDate) missing.push("Production date");
    if (!peerReviewer.trim()) missing.push("Peer reviewer");
    for (const q of selectedApp.questions || []) {
      if (q.type === "file" || !q.required) continue;
      if (!isQuestionVisible(q, answers)) continue;
      const value = answers[q.id];
      if (value == null || value === "" || (Array.isArray(value) && !value.length)) {
        missing.push(q.label);
      }
    }
    return missing;
  }, [selectedApp, changeType, summary, storySysId, crNumber, productionDate, peerReviewer, answers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedApp || submitting) return;
    setSubmitError("");

    if (missingRequired.length) {
      setSubmitError(`Please complete: ${missingRequired.join("; ")}`);
      return;
    }

    const oversized = Object.values(files).filter(f => f && f.size > MAX_ATTACHMENT_BYTES);
    if (oversized.length) {
      setSubmitError(`These files exceed 4 MB: ${oversized.map(f => f.name).join(", ")}`);
      return;
    }

    setSubmitting(true);
    try {
      const attachments = [];
      for (const [questionId, file] of Object.entries(files)) {
        if (!file) continue;
        attachments.push({
          questionId,
          filename: file.name,
          contentType: file.type || "application/octet-stream",
          contentBase64: await fileToBase64(file),
        });
      }

      const story = stories.find(s => s.sys_id === storySysId);
      const data = await apiRequest("/change-log", {
        method: "POST",
        body: JSON.stringify({
          application: selectedApp.id,
          changeType,
          summary: summary.trim(),
          description: description.trim(),
          storySysId,
          storyNumber: story?.number || "",
          storyDescription: story?.title || "",
          changeRequest: crNumber.trim().toUpperCase(),
          productionDate,
          peerReviewer: peerReviewer.trim(),
          answers,
          attachments,
        }),
      });

      setSubmitResult(data);
      resetForm();
      setView("entries");
      loadEntries();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredEntries = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return entries.filter(entry => {
      if (filterApp && entry.application !== filterApp) return false;
      if (!needle) return true;
      return [
        entry.summary, entry.storyNumber, entry.changeRequest,
        entry.submittedBy, entry.peerReviewer, entry.applicationName,
      ].some(v => String(v || "").toLowerCase().includes(needle));
    });
  }, [entries, filterApp, search]);

  const downloadAttachment = async (entry, attachment) => {
    try {
      const data = await apiRequest(
        `/change-log/attachment?entryId=${encodeURIComponent(entry.id)}&key=${encodeURIComponent(attachment.key)}`
      );
      window.open(data.url, "_blank", "noopener");
    } catch (e) {
      alert(`Could not download ${attachment.filename}: ${e.message}`);
    }
  };

  const renderQuestion = (question) => {
    if (!isQuestionVisible(question, answers)) return null;
    const value = answers[question.id];

    switch (question.type) {
      case "boolean":
        return (
          <Field key={question.id} label={question.label} required={question.required} span2>
            <BooleanToggle value={value} onChange={v => setAnswer(question.id, v)} disabled={submitting} />
          </Field>
        );
      case "multiselect":
        return (
          <Field key={question.id} label={question.label} required={question.required} span2>
            <div className="cl-checks">
              {(question.options || []).map(option => {
                const selected = Array.isArray(value) && value.includes(option);
                return (
                  <label key={option} className="cl-check">
                    <input
                      type="checkbox"
                      checked={selected}
                      disabled={submitting}
                      onChange={() => {
                        const current = Array.isArray(value) ? value : [];
                        setAnswer(
                          question.id,
                          selected ? current.filter(v => v !== option) : [...current, option]
                        );
                      }}
                    />
                    {option}
                  </label>
                );
              })}
            </div>
          </Field>
        );
      case "select":
        return (
          <Field key={question.id} label={question.label} required={question.required} span2>
            <select className="cug-input" value={value || ""} disabled={submitting} onChange={e => setAnswer(question.id, e.target.value)}>
              <option value="">Select…</option>
              {(question.options || []).map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          </Field>
        );
      case "textarea":
        return (
          <Field key={question.id} label={question.label} required={question.required} span2>
            <textarea
              className="cug-input cug-textarea"
              rows={3}
              value={value || ""}
              disabled={submitting}
              onChange={e => setAnswer(question.id, e.target.value)}
            />
          </Field>
        );
      case "date":
        return (
          <Field key={question.id} label={question.label} required={question.required}>
            <input type="date" className="cug-input" value={value || ""} disabled={submitting} onChange={e => setAnswer(question.id, e.target.value)} />
          </Field>
        );
      case "file":
        return (
          <Field key={question.id} label={question.label} required={question.required} hint="Max 4 MB per file" span2>
            <input
              type="file"
              className="cl-file-input"
              disabled={submitting}
              onChange={e => setFiles(prev => ({ ...prev, [question.id]: e.target.files?.[0] || null }))}
            />
            {files[question.id] && (
              <div className="cl-hint">
                {files[question.id].name} ({Math.ceil(files[question.id].size / 1024)} KB)
              </div>
            )}
          </Field>
        );
      default:
        return (
          <Field key={question.id} label={question.label} required={question.required} span2>
            <input className="cug-input" value={value || ""} disabled={submitting} onChange={e => setAnswer(question.id, e.target.value)} />
          </Field>
        );
    }
  };

  return (
    <div className="as-root">
      {/* ── Header ── */}
      <div className="as-header">
        <div className="as-header-inner">
          <div className="logo-mark">C</div>
          <div>
            <div className="as-title">Change Log</div>
            <div className="as-subtitle">Production promotion checklist for supported applications</div>
          </div>
        </div>
      </div>

      <div className="cl-body">
        <div className="cl-tabs">
          <button
            className={`cl-tab${view === "entries" ? " cl-tab-active" : ""}`}
            onClick={() => setView("entries")}
          >
            Entries
          </button>
          <button
            className={`cl-tab${view === "new" ? " cl-tab-active" : ""}`}
            onClick={() => { setSubmitResult(null); setView("new"); }}
          >
            + New Entry
          </button>
        </div>
        {configError && (
          <div className="cl-banner cl-banner-error">
            Could not load checklist configuration: {configError}
          </div>
        )}

        {submitResult && view === "entries" && (
          <div className="cl-banner cl-banner-success">
            <div className="cl-banner-title">Change log entry submitted</div>
            <div className="cl-banner-copy">
              {submitResult.workNotePosted
                ? `The checklist answers were posted as a work note on story ${submitResult.entry?.storyNumber || ""}.`
                : `The entry was saved, but posting the work note to the story failed${submitResult.workNoteError ? `: ${submitResult.workNoteError}` : "."} Add the note manually in ServiceNow.`}
            </div>
          </div>
        )}

        {/* ── Entries view ── */}
        {view === "entries" && (
          <>
            <div className="cl-toolbar">
              <select className="cug-input cl-toolbar-select" value={filterApp} onChange={e => setFilterApp(e.target.value)}>
                <option value="">All applications</option>
                {applications.map(app => <option key={app.id} value={app.id}>{app.name}</option>)}
              </select>
              <input
                className="cug-input cl-toolbar-search"
                placeholder="Search summary, story, CR, developer…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <button className="cug-reset-btn" onClick={loadEntries} disabled={entriesLoading}>
                {entriesLoading ? "Refreshing…" : "Refresh"}
              </button>
            </div>

            {entriesError && <div className="cl-banner cl-banner-error">{entriesError}</div>}

            <div className="cl-card cl-table-card">
              <table className="cl-table">
                <thead>
                  <tr>
                    <th>Submitted</th>
                    <th>Application</th>
                    <th>Type</th>
                    <th>Summary</th>
                    <th>Story</th>
                    <th>CR</th>
                    <th>Prod date</th>
                    <th>Developer</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.length === 0 && (
                    <tr style={{ cursor: "default" }}>
                      <td colSpan={8} className="cl-td-empty">
                        {entriesLoading ? "Loading…" : "No change log entries yet."}
                      </td>
                    </tr>
                  )}
                  {filteredEntries.map(entry => (
                    <tr key={entry.id} onClick={() => setSelectedEntry(entry)}>
                      <td className="cl-td-nowrap">{formatDateTime(entry.submittedAt)}</td>
                      <td className="cl-td-nowrap">{entry.applicationName || entry.application}</td>
                      <td>
                        <span className={`cl-pill cl-pill-${entry.changeType || "enhancement"}`}>
                          {changeTypeLabel(entry.changeType)}
                        </span>
                      </td>
                      <td className="cl-td-summary" title={entry.summary}>{entry.summary}</td>
                      <td className="cl-td-nowrap">{entry.storyNumber || "-"}</td>
                      <td className="cl-td-nowrap">{entry.changeRequest}</td>
                      <td className="cl-td-nowrap">{entry.productionDate}</td>
                      <td className="cl-td-nowrap">{entry.submittedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── New entry form ── */}
        {view === "new" && (
          <form onSubmit={handleSubmit}>
            <div className="cl-card">
              <h3 className="cl-card-title">Change details</h3>
              <div className="cl-grid-2">
                <Field label="Application" required>
                  <select className="cug-input" value={application} disabled={submitting} onChange={e => handleApplicationChange(e.target.value)}>
                    <option value="">Select application…</option>
                    {applications.map(app => <option key={app.id} value={app.id}>{app.name}</option>)}
                  </select>
                </Field>
                <Field label="Change type" required>
                  <select className="cug-input" value={changeType} disabled={submitting} onChange={e => setChangeType(e.target.value)}>
                    <option value="">Select type…</option>
                    {CHANGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </Field>
                <Field label="Summary" required span2>
                  <input className="cug-input" value={summary} disabled={submitting} maxLength={200} placeholder="One-line description of the change" onChange={e => setSummary(e.target.value)} />
                </Field>
                <Field label="Description" span2>
                  <textarea className="cug-input cug-textarea" rows={3} value={description} disabled={submitting} placeholder="Optional additional context" onChange={e => setDescription(e.target.value)} />
                </Field>
              </div>
            </div>

            <div className="cl-card">
              <h3 className="cl-card-title">Story &amp; Change Request</h3>
              <div className="cl-grid-2">
                <Field label="Sprint" required hint={sprintsLoading ? "Loading sprints…" : ""}>
                  <select className="cug-input" value={sprintId} disabled={submitting || sprintsLoading} onChange={e => setSprintId(e.target.value)}>
                    <option value="">Select sprint…</option>
                    {sprints.map(s => <option key={s.sys_id} value={s.sys_id}>{s.name}</option>)}
                  </select>
                </Field>
                <Field label="Story" required hint={storiesLoading ? "Loading stories…" : ""}>
                  <select className="cug-input" value={storySysId} disabled={submitting || !sprintId || storiesLoading} onChange={e => setStorySysId(e.target.value)}>
                    <option value="">{sprintId ? "Select story…" : "Select a sprint first"}</option>
                    {stories.map(s => (
                      <option key={s.sys_id} value={s.sys_id}>
                        {(s.number ? `${s.number} — ` : "") + (s.title || "")}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Change Request number" required>
                  <div className="cl-inline">
                    <input
                      className="cug-input"
                      placeholder="CHG0012345"
                      value={crNumber}
                      disabled={submitting}
                      onChange={e => { setCrNumber(e.target.value); setCrStatus(null); }}
                    />
                    <button type="button" className="cug-reset-btn" onClick={handleValidateCr} disabled={submitting || !crNumber.trim() || crStatus === "checking"}>
                      {crStatus === "checking" ? "Checking…" : "Validate"}
                    </button>
                  </div>
                  {crStatus && crStatus !== "checking" && (
                    <div className={crStatus.valid ? "cl-valid" : "cl-invalid"}>
                      {crStatus.error
                        ? crStatus.error
                        : crStatus.valid
                          ? `✓ ${crStatus.number}: ${crStatus.shortDescription || "found"}${crStatus.state ? ` (${crStatus.state})` : ""}`
                          : `✗ ${crStatus.number} was not found in ServiceNow`}
                    </div>
                  )}
                </Field>
                <Field label="Planned production date" required>
                  <input type="date" className="cug-input" value={productionDate} disabled={submitting} onChange={e => setProductionDate(e.target.value)} />
                </Field>
                <Field label="Peer reviewed by" required span2>
                  <input className="cug-input" value={peerReviewer} disabled={submitting} placeholder="Name of the peer reviewer" onChange={e => setPeerReviewer(e.target.value)} />
                </Field>
              </div>
            </div>

            {selectedApp ? (
              <div className="cl-card">
                <h3 className="cl-card-title">{selectedApp.name} checklist</h3>
                <div className="cl-grid-2">
                  {(selectedApp.questions || []).map(renderQuestion)}
                </div>
              </div>
            ) : (
              <div className="cl-card">
                <div className="cl-card-empty">Select an application to see its promotion checklist.</div>
              </div>
            )}

            {submitError && <div className="cl-banner cl-banner-error">{submitError}</div>}

            <div className="cl-form-actions">
              <button type="submit" className="compare-btn" disabled={submitting || !selectedApp}>
                {submitting ? "Submitting…" : "Submit change log entry"}
              </button>
              <button type="button" className="cug-reset-btn" disabled={submitting} onClick={() => { resetForm(); setView("entries"); }}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* ── Detail modal ── */}
      {selectedEntry && (
        <div className="modal-overlay" onClick={() => setSelectedEntry(null)}>
          <div className="modal-card cl-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Log Entry — {selectedEntry.applicationName || selectedEntry.application}</h3>
              <button className="modal-close-btn" onClick={() => setSelectedEntry(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="cl-detail-grid">
                {[
                  ["Submitted", formatDateTime(selectedEntry.submittedAt)],
                  ["Submitted by", `${selectedEntry.submittedBy || "-"}${selectedEntry.submittedByEmail ? ` (${selectedEntry.submittedByEmail})` : ""}`],
                  ["Change type", changeTypeLabel(selectedEntry.changeType)],
                  ["Peer reviewed by", selectedEntry.peerReviewer || "-"],
                  ["Story", selectedEntry.storyNumber ? `${selectedEntry.storyNumber}${selectedEntry.storyDescription ? ` — ${selectedEntry.storyDescription}` : ""}` : "-"],
                  ["Change Request", selectedEntry.changeRequest || "-"],
                  ["Production date", selectedEntry.productionDate || "-"],
                  ["Work note posted to story", selectedEntry.workNotePosted ? "Yes" : "No"],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div className="cl-detail-label">{label}</div>
                    <div className="cl-detail-value">{value}</div>
                  </div>
                ))}
              </div>

              <div className="cl-detail-section">
                <div className="cl-detail-label">Summary</div>
                <div className="cl-detail-value">{selectedEntry.summary}</div>
                {selectedEntry.description && (
                  <div className="cl-detail-copy">{selectedEntry.description}</div>
                )}
              </div>

              <div className="cl-detail-section">
                <div className="cl-detail-label">Checklist answers</div>
                {(() => {
                  const app = applications.find(a => a.id === selectedEntry.application);
                  const questionMap = new Map((app?.questions || []).map(q => [q.id, q]));
                  const entryAnswers = selectedEntry.answers || {};
                  const orderedIds = [
                    ...(app?.questions || []).map(q => q.id).filter(id => id in entryAnswers),
                    ...Object.keys(entryAnswers).filter(id => !questionMap.has(id)),
                  ];
                  return orderedIds.map(id => {
                    const question = questionMap.get(id);
                    return (
                      <div key={id} className="cl-qa-row">
                        <div className="cl-qa-question">{question?.label || id}</div>
                        <div className="cl-qa-answer">{answerDisplay(question, entryAnswers[id])}</div>
                      </div>
                    );
                  });
                })()}
              </div>

              {(selectedEntry.attachments || []).length > 0 && (
                <div className="cl-detail-section">
                  <div className="cl-detail-label">Attachments</div>
                  <div className="cl-attachment-list">
                    {selectedEntry.attachments.map(attachment => (
                      <button
                        key={attachment.key}
                        type="button"
                        className="cug-reset-btn"
                        onClick={() => downloadAttachment(selectedEntry, attachment)}
                      >
                        ⬇ {attachment.filename} ({Math.ceil((attachment.sizeBytes || 0) / 1024)} KB)
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
